export const PORTAL_THEME_STORAGE_KEY = 'yandle-portal-theme'

/** Dark palette — current portal look (indigo / slate glass). */
export const portalDarkThemeColors = {
  background: '#030712',
  'background-subtle': '#0f172a',
  surface: '#0f172a',
  primary: '#0544e2',
  'primary-darken-1': '#0439c4',
  secondary: '#64748b',
  accent: '#2563eb',
  error: '#ef4444',
  success: '#22c55e',
  warning: '#f59e0b',
  info: '#3b82f6',

  'on-background': '#ffffff',
  'on-surface': '#cbd5e1',

  'text-muted': '#94a3b8',
  'text-inverse': '#f1f5f9',
  border: '#1e293b',
  'border-strong': '#334155',
  'surface-hover': '#1e293b',

  'shell-from': '#0f172a',
  'shell-to': '#1e293b',
  'shell-mid': '#1e293b',
  'shell-deep': '#030712',
  'shell-border': '#334155',
  'shell-surface': '#1e293b',
  'shell-hover': '#273449',
  'shell-active': '#312e81',

  'primary-muted': '#1e1b4b',
  'accent-muted': '#2e1065',
  'error-muted': '#450a0a',
  'success-muted': '#14532d',
  'warning-muted': '#451a03',
  'info-muted': '#1e3a5f',

  'table-header': '#0f172a',
  'table-row-hover': '#1e1b4b',
  'table-row-stripe': '#0a0f1a',
}

/** Light palette — futuristic indigo / silver glass (complements dark). */
export const portalLightThemeColors = {
  background: '#dbeafe',
  'background-subtle': '#e0e7ff',
  surface: '#ffffff',
  primary: '#0544e2',
  'primary-darken-1': '#0439c4',
  secondary: '#64748b',
  accent: '#2563eb',
  error: '#ef4444',
  success: '#22c55e',
  warning: '#f59e0b',
  info: '#3b82f6',

  'on-background': '#1e1b4b',
  'on-surface': '#334155',

  'text-muted': '#64748b',
  'text-inverse': '#f8fafc',
  border: '#c7d2fe',
  'border-strong': '#a5b4fc',
  'surface-hover': '#eef2ff',

  'shell-from': '#ffffff',
  'shell-to': '#f8fafc',
  'shell-mid': '#f1f5f9',
  'shell-deep': '#eef2ff',
  'shell-border': '#c7d2fe',
  'shell-surface': '#ffffff',
  'shell-hover': '#eef2ff',
  'shell-active': '#e0e7ff',

  'primary-muted': '#eef2ff',
  'accent-muted': '#f5f3ff',
  'error-muted': '#fef2f2',
  'success-muted': '#f0fdf4',
  'warning-muted': '#fffbeb',
  'info-muted': '#eff6ff',

  'table-header': 'rgba(255, 255, 255, 0.88)',
  'table-row-hover': '#eef2ff',
  'table-row-stripe': 'rgba(255, 255, 255, 0.55)',
}

// Light is the portal's default appearance (Ramu, 2026-09-04) -- a visitor
// who has never toggled the theme (no stored preference yet) gets light,
// same as a first-time visit to the login screen outside the authenticated
// shell. Once someone explicitly switches, that choice is what's stored and
// respected here from then on -- this default only applies pre-choice.
export function getStoredPortalTheme() {
  if (typeof localStorage === 'undefined') return 'light'
  const stored = localStorage.getItem(PORTAL_THEME_STORAGE_KEY)
  return stored === 'dark' ? 'dark' : 'light'
}

export function persistPortalTheme(name) {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(PORTAL_THEME_STORAGE_KEY, name)
}

function getThemeGlobal(vuetifyOrTheme) {
  if (!vuetifyOrTheme) return null
  if (vuetifyOrTheme.theme?.global) return vuetifyOrTheme.theme.global
  if (vuetifyOrTheme.global) return vuetifyOrTheme.global
  return null
}

function getThemeNameRef(vuetifyOrTheme) {
  return getThemeGlobal(vuetifyOrTheme)?.name
}

export function getPortalThemeName(vuetifyOrTheme) {
  const name = getThemeNameRef(vuetifyOrTheme)
  if (name != null && typeof name === 'object' && 'value' in name) {
    return name.value
  }
  return name ?? getStoredPortalTheme()
}

export function syncPortalThemeClass(name) {
  if (typeof document === 'undefined') return
  const theme = name === 'light' ? 'light' : 'dark'
  document.documentElement.classList.remove('v-theme--dark', 'v-theme--light')
  document.documentElement.classList.add(`v-theme--${theme}`)
  document.documentElement.setAttribute('data-portal-theme', theme)
}

export function setPortalThemeName(vuetifyOrTheme, name) {
  const themeName = getThemeNameRef(vuetifyOrTheme)
  if (themeName != null && typeof themeName === 'object' && 'value' in themeName) {
    themeName.value = name
  } else {
    const global = getThemeGlobal(vuetifyOrTheme)
    if (global) global.name = name
  }
  syncPortalThemeClass(name)
}

export function initPortalTheme(vuetify) {
  const name = getStoredPortalTheme()
  setPortalThemeName(vuetify, name)
}

export function togglePortalTheme(vuetify) {
  const next = getPortalThemeName(vuetify) === 'dark' ? 'light' : 'dark'
  setPortalThemeName(vuetify, next)
  persistPortalTheme(next)
  return next
}

export function isPortalDarkTheme(vuetify) {
  return getPortalThemeName(vuetify) === 'dark'
}
